import {
  DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  parseResponse,
} from "@softmax/observatory-client/transport";
import { withRequestDeadline } from "@softmax/observatory-client/request-signal";
import type { Schemas } from "@softmax/observatory-client/types";

export const sessionChangeEvent = "softmax:session-change";
export const sessionChannel = "next-auth";
export const sessionEventSource = crypto.randomUUID();

export function announceSessionChange({
  kind = "changed",
  notifyCurrentTab = true,
}: {
  readonly kind?: ("changed" | "signed-out" | "invalidated") | undefined;
  readonly notifyCurrentTab?: boolean;
}) {
  if (notifyCurrentTab)
    window.dispatchEvent(new CustomEvent(sessionChangeEvent, { detail: kind }));

  const channel = new BroadcastChannel(sessionChannel);
  const message = {
    event: "session",
    source: sessionEventSource,
    data: {
      trigger: {
        changed: "getSession",
        "signed-out": "signout",
        invalidated: "invalidated",
      }[kind],
    },
  };
  // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel messages stay on this origin and take no target origin.
  channel.postMessage(message);
  channel.close();
}

async function startAuthorization({
  provider,
  callbackUrl = window.location.href,
  intent,
  userId,
}: {
  readonly provider: "google" | "github" | "discord";
  readonly callbackUrl?: string;
  readonly intent: "signin" | "link";
  readonly userId?: string;
}): Promise<void> {
  const { csrfToken } = await fetch("/api/auth/csrf", {
    cache: "no-store",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
  }).then((response) =>
    parseResponse<Schemas["WebsiteCsrfResponse"]>(response),
  );
  const body = new URLSearchParams({ csrfToken, callbackUrl, intent });

  if (userId) body.set("userId", userId);

  const response = await fetch(`/api/auth/signin/${provider}`, {
    method: "POST",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Auth-Return-Redirect": "1",
    },
    body,
  });

  if (response.status === 409) announceSessionChange({ kind: "invalidated" });

  const { url } =
    await parseResponse<Schemas["WebsiteRedirectResponse"]>(response);
  window.location.assign(url);
}

export function signIn({
  provider,
  callbackUrl = window.location.href,
}: {
  readonly provider: "google" | "github" | "discord";
  readonly callbackUrl?: string;
}): Promise<void> {
  return startAuthorization({ provider, callbackUrl, intent: "signin" });
}

export function linkAccount({
  provider,
  userId,
  callbackUrl = window.location.href,
}: {
  readonly provider: "google" | "github" | "discord";
  readonly userId: string;
  readonly callbackUrl?: string;
}): Promise<void> {
  return startAuthorization({ provider, callbackUrl, userId, intent: "link" });
}

export async function signOut({
  callbackUrl = window.location.href,
}: {
  readonly callbackUrl?: string;
} = {}): Promise<void> {
  const { csrfToken } = await fetch("/api/auth/csrf", {
    cache: "no-store",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
  }).then((response) =>
    parseResponse<Schemas["WebsiteCsrfResponse"]>(response),
  );
  const response = await fetch("/api/auth/signout", {
    method: "POST",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Auth-Return-Redirect": "1",
    },
    body: new URLSearchParams({ csrfToken, callbackUrl }),
  });
  const { url } =
    await parseResponse<Schemas["WebsiteRedirectResponse"]>(response);
  announceSessionChange({ kind: "signed-out", notifyCurrentTab: false });
  window.location.assign(url);
}
